import { useState, useEffect, useCallback } from 'react'
import { AdminLayout, PageHeader } from '@/components/layout/AdminLayout'
import { fetchAdminOptions, fetchAdminCategories, updateOptionPrice } from '@/lib/api'
import type { OptionWithModels } from '@/lib/api'
import type { Category } from '@/types'

function PriceInput({ option, onSaved }: { option: OptionWithModels; onSaved: (id: number, price: number) => void }) {
  const [price, setPrice] = useState(String(option.price))
  const [saving, setSaving] = useState(false)
  const [flash, setFlash] = useState(false)
  const dirty = Number(price) !== option.price

  async function save() {
    if (!dirty || isNaN(Number(price))) return
    setSaving(true)
    try {
      await updateOptionPrice(option.id, Number(price))
      onSaved(option.id, Number(price))
      setFlash(true)
      setTimeout(() => setFlash(false), 1500)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-slate-400 text-sm">$</span>
      <input
        type="number"
        min={0}
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === 'Enter') save() }}
        className={`w-28 border rounded-lg px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-brand-400 transition-colors ${
          flash ? 'border-green-400 bg-green-50' : dirty ? 'border-brand-400 bg-brand-50' : 'border-slate-200'
        }`}
        disabled={saving}
      />
      {saving && <span className="text-xs text-slate-400">Saving…</span>}
      {flash && <span className="text-xs text-green-600 font-medium">Saved</span>}
    </div>
  )
}

export default function AdminPriceValve() {
  const [options, setOptions] = useState<OptionWithModels[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([fetchAdminOptions(), fetchAdminCategories()])
      .then(([opts, cats]) => { setOptions(opts); setCategories(cats) })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  const handleSaved = useCallback((id: number, price: number) => {
    setOptions((prev) => prev.map((o) => o.id === id ? { ...o, price } : o))
  }, [])

  if (loading) return (
    <AdminLayout>
      <PageHeader title="Price Valve" subtitle="Update option prices in one place" />
      <div className="text-slate-400 text-sm">Loading…</div>
    </AdminLayout>
  )

  if (error) return (
    <AdminLayout>
      <PageHeader title="Price Valve" subtitle="Update option prices in one place" />
      <div className="text-red-600 text-sm">{error}</div>
    </AdminLayout>
  )

  const catMap = Object.fromEntries(categories.map((c) => [c.id, c.name]))

  const byCategory = categories.map((cat) => ({
    cat,
    opts: options.filter((o) => o.category_id === cat.id),
  })).filter((g) => g.opts.length > 0)

  return (
    <AdminLayout>
      <PageHeader title="Price Valve" subtitle="Click any price to edit — saves on blur or Enter" />

      <div className="space-y-6">
        {byCategory.map(({ cat, opts }) => (
          <div key={cat.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700">{cat.name}</h3>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">Option</th>
                  <th className="text-left px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide hidden sm:table-cell">Category</th>
                  <th className="text-right px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">Price</th>
                </tr>
              </thead>
              <tbody>
                {opts.map((opt) => (
                  <tr key={opt.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                    <td className="px-5 py-3 text-slate-800 font-medium">{opt.name}</td>
                    <td className="px-5 py-3 text-slate-500 hidden sm:table-cell">{catMap[opt.category_id]}</td>
                    <td className="px-5 py-3 text-right">
                      <PriceInput option={opt} onSaved={handleSaved} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </AdminLayout>
  )
}
